import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [tier, setTier] = useState('');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
          setTier(res.data.tier || '');
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };
    poll();
  }, [sessionId, attempts]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#f8f9ff] via-white to-[#eff4ff] relative overflow-hidden" data-testid="payment-success-page" style={{ fontFamily: "'Inter', sans-serif" }}>
      <style>{`
        @keyframes slowSpinPay { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-slowSpinPay { animation: slowSpinPay 40s linear infinite; }
        .grid-bg-pay { background-image: linear-gradient(rgba(0,74,198,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0,74,198,0.05) 1px, transparent 1px); background-size: 40px 40px; }
      `}</style>
      <div className="absolute inset-0 grid-bg-pay opacity-60 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-gradient-to-br from-[#004ac6]/5 via-[#7c3aed]/5 to-transparent animate-slowSpinPay pointer-events-none" />
      <div className="relative z-10 bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center border border-[#c3c6d7]/30">
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-[#004ac6] mx-auto animate-spin mb-4" />
            <h2 className="text-xl font-extrabold text-[#0b1c30] mb-2">Verifying Payment...</h2>
            <p className="text-sm text-[#434655]">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-extrabold text-[#0b1c30] mb-2">Payment Successful!</h2>
            <p className="text-sm text-[#434655] mb-1">You are now subscribed to the <strong className="text-[#004ac6]">{tier}</strong> plan.</p>
            <p className="text-xs text-[#737686] mb-6">You can now create unlimited country blueprints.</p>
            <button onClick={() => navigate('/platform/countries')} data-testid="go-to-countries"
              className="bg-[#004ac6] text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-[#003ea8] transition-all flex items-center gap-2 mx-auto">
              Start Creating Reports <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-extrabold text-[#0b1c30] mb-2">
              {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
            </h2>
            <p className="text-sm text-[#434655] mb-6">
              {status === 'expired' ? 'Your payment session has expired. Please try again.' : 'We could not verify your payment. Please contact support or try again.'}
            </p>
            <button onClick={() => navigate('/pricing')} className="bg-[#004ac6] text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-[#003ea8] transition-all mx-auto">
              Back to Pricing
            </button>
          </>
        )}
      </div>
    </div>
  );
}
